import {
  ArmchairIcon,
  CalendarBlankIcon,
  EngineIcon,
  GasPumpIcon,
  GaugeIcon,
  GearSixIcon,
  LeafIcon,
  PaletteIcon,
} from '@phosphor-icons/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { formatMileage, fuelLabelKey, registrationYear, transmissionLabelKey } from './lib';
import type { VehicleSpecs } from './types';
import styles from './VehicleKeySpecs.module.css';

/**
 * Метрическая лошадиная сила (PS) в киловаттах: 1 PS = 0,73549875 kW.
 * Пересчёт — арифметика над числом из объявления, а не новое значение: в
 * Германии мощность называют в обеих единицах, «135 kW (184 PS)».
 */
const KW_PER_PS = 0.73549875;

interface KeySpec {
  key: 'year' | 'mileage' | 'fuel' | 'transmission' | 'power' | 'co2' | 'color' | 'seats';
  icon: ReactNode;
  /** `null` — в объявлении значения нет: показывается «Keine Angabe», а не прочерк и не ноль. */
  value: string | null;
}

/**
 * Восемь главных характеристик рядом с ценой (FE-587, макет 04): год, пробег,
 * топливо, коробка, мощность, CO₂, цвет, места.
 *
 * Пустое значение **не скрывается и не подменяется**: «Keine Angabe» вместо
 * прочерка и нуля — ноль мест или ноль граммов CO₂ были бы утверждением, а не
 * отсутствием данных (MVP_PRD §18). Полная таблица — `VehicleDetails` ниже на
 * экране; здесь только то, по чему автомобиль узнают с первого взгляда.
 *
 * Иконки декоративны: смысл несёт подпись (DESIGN_SYSTEM §5.10).
 */
export function VehicleKeySpecs({ specs, locale }: { specs: VehicleSpecs; locale: string }) {
  const { t } = useTranslation();
  const noData = t('catalog.noData');

  const rows: KeySpec[] = [
    {
      key: 'year',
      icon: <CalendarBlankIcon size={20} aria-hidden="true" />,
      value: registrationYear(specs.first_registration_date),
    },
    {
      key: 'mileage',
      icon: <GaugeIcon size={20} aria-hidden="true" />,
      value: t('catalog.spec.mileageValue', { value: formatMileage(specs.mileage_km, locale) }),
    },
    {
      key: 'fuel',
      icon: <GasPumpIcon size={20} aria-hidden="true" />,
      value: t(fuelLabelKey(specs.fuel_type)),
    },
    {
      key: 'transmission',
      icon: <GearSixIcon size={20} aria-hidden="true" />,
      value: t(transmissionLabelKey(specs.transmission)),
    },
    {
      key: 'power',
      icon: <EngineIcon size={20} aria-hidden="true" />,
      value:
        specs.power_kw === null
          ? null
          : t('vehicle.keySpecs.powerValue', {
              kw: new Intl.NumberFormat(locale).format(specs.power_kw),
              ps: new Intl.NumberFormat(locale).format(Math.round(specs.power_kw / KW_PER_PS)),
            }),
    },
    {
      key: 'co2',
      icon: <LeafIcon size={20} aria-hidden="true" />,
      value:
        specs.co2_g_km === null
          ? null
          : t('vehicle.specs.co2Value', {
              value: new Intl.NumberFormat(locale).format(specs.co2_g_km),
            }),
    },
    {
      key: 'color',
      icon: <PaletteIcon size={20} aria-hidden="true" />,
      value: specs.exterior_color,
    },
    {
      key: 'seats',
      icon: <ArmchairIcon size={20} aria-hidden="true" />,
      value: specs.seats === null ? null : String(specs.seats),
    },
  ];

  return (
    <dl className={styles.specs}>
      {rows.map((row) => (
        <div key={row.key} className={styles.row}>
          {/* Иконка — внутри `dt`: в `dl > div` допустимы только `dt` и `dd`. */}
          <dt className={styles.label}>
            <span className={styles.icon}>{row.icon}</span>
            {t(`vehicle.keySpecs.${row.key}`)}
          </dt>
          <dd className={row.value === null ? `${styles.value} ${styles.missing}` : styles.value}>
            {row.value ?? noData}
          </dd>
        </div>
      ))}
    </dl>
  );
}
